import { useEffect, useId, useRef, useState } from "react"

import { cn } from "~/utils/helpers"

interface HakiFaceProps {
  size: number
  animated?: boolean
  working?: boolean
  className?: string
}

const VIEWBOX = 69
const FACE_CENTER = 34.5
const FACE_RADIUS = 32.5
const STROKE = 4
// The dark face fill stops where the stroke starts. `size` represents the
// face fill diameter so callers can think of it like Figma — a 16x16 icon
// with the gradient stroke layered outside it.
const FACE_FILL_DIAMETER = VIEWBOX - 2 * STROKE
const EYE_RX = 6
const EYE_RY = 9
const EYE_Y = 27.5
const EYE_LEFT_X = 28.5
const EYE_RIGHT_X = 46.5
const MAX_EYE_TRANSLATE_USER = 10
const TRACKING_RADIUS_PX = 200

export default function HakiFace({
  size,
  animated = false,
  working = false,
  className,
}: HakiFaceProps) {
  const gradientId = useId()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const [eyeOffset, setEyeOffset] = useState({
    left: { x: 0, y: 0 },
    right: { x: 0, y: 0 },
  })
  const [blink, setBlink] = useState<"none" | "both" | "left" | "right">("none")

  useEffect(() => {
    if (!animated) return
    const svg = svgRef.current
    if (!svg) return

    const handleMove = (e: MouseEvent) => {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        const logoRect = svg.getBoundingClientRect()
        const logoCx = logoRect.left + logoRect.width / 2
        const logoCy = logoRect.top + logoRect.height / 2
        // Gate activation by distance from the logo itself.
        if (Math.hypot(e.clientX - logoCx, e.clientY - logoCy) > TRACKING_RADIUS_PX) return
        // Each eye looks at the cursor from ITS OWN screen position, so eyes
        // converge inward when the cursor is near the face center instead of
        // both translating parallel to a single shared reference.
        const scaleX = logoRect.width / VIEWBOX
        const scaleY = logoRect.height / VIEWBOX
        const eyeCy = logoCy - (FACE_CENTER - EYE_Y) * scaleY
        const leftCx = logoCx - (FACE_CENTER - EYE_LEFT_X) * scaleX
        const rightCx = logoCx + (EYE_RIGHT_X - FACE_CENTER) * scaleX
        const offsetFrom = (eyeCx: number) => {
          const dx = (e.clientX - eyeCx) / TRACKING_RADIUS_PX
          const dy = (e.clientY - eyeCy) / TRACKING_RADIUS_PX
          const mag = Math.hypot(dx, dy)
          return mag > 1 ? { x: dx / mag, y: dy / mag } : { x: dx, y: dy }
        }
        setEyeOffset({ left: offsetFrom(leftCx), right: offsetFrom(rightCx) })
      })
    }

    window.addEventListener("mousemove", handleMove)
    return () => {
      window.removeEventListener("mousemove", handleMove)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [animated])

  useEffect(() => {
    if (!working) {
      setBlink("none")
      return
    }
    let cancelled = false
    let timeout: ReturnType<typeof setTimeout> | undefined
    const scheduleNext = () => {
      const delay = 3000 + Math.random() * 3000
      timeout = setTimeout(() => {
        if (cancelled) return
        const isWink = Math.random() < 0.2
        const nextBlink: "both" | "left" | "right" = isWink
          ? Math.random() < 0.5
            ? "left"
            : "right"
          : "both"
        setBlink(nextBlink)
        timeout = setTimeout(() => {
          if (cancelled) return
          setBlink("none")
          scheduleNext()
        }, 140)
      }, delay)
    }
    scheduleNext()
    return () => {
      cancelled = true
      if (timeout != null) clearTimeout(timeout)
    }
  }, [working])

  const renderPx = (size * VIEWBOX) / FACE_FILL_DIAMETER
  const pxPerUserUnit = renderPx / VIEWBOX
  const eyeGroupStyle = (offset: { x: number; y: number }): React.CSSProperties => ({
    transform: `translate(${offset.x * MAX_EYE_TRANSLATE_USER * pxPerUserUnit}px, ${
      offset.y * MAX_EYE_TRANSLATE_USER * pxPerUserUnit
    }px)`,
    transition: "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
  })

  const eyeStyle = (blinking: boolean): React.CSSProperties => ({
    transform: blinking ? "scaleY(0.05)" : "scaleY(1)",
    transformBox: "fill-box",
    transformOrigin: "center",
    transition: "transform 90ms ease-in-out",
  })
  const leftBlinking = blink === "both" || blink === "left"
  const rightBlinking = blink === "both" || blink === "right"

  return (
    <svg
      ref={svgRef}
      className={cn("shrink-0", className)}
      width={renderPx}
      height={renderPx}
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        cx={FACE_CENTER}
        cy={FACE_CENTER}
        r={FACE_RADIUS}
        fill="var(--color-neutral-950)"
        stroke={`url(#${gradientId})`}
        strokeWidth={STROKE}
      />
      <g style={eyeGroupStyle(eyeOffset.left)}>
        <ellipse
          cx={EYE_LEFT_X}
          cy={EYE_Y}
          rx={EYE_RX}
          ry={EYE_RY}
          fill="#fff"
          style={eyeStyle(leftBlinking)}
        />
      </g>
      <g style={eyeGroupStyle(eyeOffset.right)}>
        <ellipse
          cx={EYE_RIGHT_X}
          cy={EYE_Y}
          rx={EYE_RX}
          ry={EYE_RY}
          fill="#fff"
          style={eyeStyle(rightBlinking)}
        />
      </g>
      <defs>
        <linearGradient
          id={gradientId}
          x1={2.5}
          y1={FACE_CENTER}
          x2={66.5}
          y2={FACE_CENTER}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FDBA74" />
          <stop offset={0.33} stopColor="#FDA4AF" />
          <stop offset={0.66} stopColor="#C4B5FD" />
          <stop offset={1} stopColor="#93C5FD" />
        </linearGradient>
      </defs>
    </svg>
  )
}
